import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { setTrackingFunction } from '../lib/api/supabaseWithTracking';
import { Icon } from './ui/Icon';
import { ICONS_MAP } from '../utils/constants';

/**
 * Simple Sync Indicator
 * Shows a rotating sync icon with count during sync operations
 * Shows checkmark briefly when complete, then hides
 */

const SyncContext = createContext();

export const useSyncStatus = () => {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSyncStatus must be used within SyncStatusProvider');
  }
  return context;
};

export const SyncStatusProvider = ({ children }) => {
  const [operations, setOperations] = useState([]);
  const [isVisible, setIsVisible] = useState(true);
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);

  // Track page visibility to handle browser minimize/restore
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      setIsPageVisible(visible);

      if (visible) {
        console.log('🔄 Page became visible - sync operations will resume');
      } else {
        console.log('⏸️  Page hidden - sync operations may be paused by browser');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Helper functions for common operations
  const trackOperation = useCallback(async (type, table, action, promise) => {
    const opId = addOperation({
      type, // 'insert', 'update', 'delete', 'fetch'
      table, // 'customers', 'products', etc.
      action, // description of the action
      status: 'pending'
    });

    try {
      const result = await promise;
      updateOperation(opId, {
        status: 'success',
        completedAt: new Date().toISOString()
      });
      return result;
    } catch (error) {
      updateOperation(opId, {
        status: 'error',
        error: error.message,
        completedAt: new Date().toISOString()
      });
      throw error;
    }
  }, []);

  const addOperation = useCallback((operation) => {
    const id = `${Date.now()}_${Math.random()}`;
    const newOp = {
      id,
      ...operation,
      timestamp: new Date().toISOString(),
      status: 'pending' // pending, success, error
    };

    setOperations(prev => [newOp, ...prev].slice(0, 50)); // Keep last 50 operations
    return id;
  }, []);

  const updateOperation = useCallback((id, updates) => {
    setOperations(prev =>
      prev.map(op => op.id === id ? { ...op, ...updates } : op)
    );
  }, []);

  const clearOperations = useCallback(() => {
    setOperations([]);
  }, []);

  const toggleVisibility = useCallback(() => {
    setIsVisible(prev => !prev);
  }, []);

  // Register tracking function with supabaseWithTracking
  useEffect(() => {
    setTrackingFunction(trackOperation);
  }, [trackOperation]);

  const value = {
    operations,
    isVisible,
    addOperation,
    updateOperation,
    clearOperations,
    toggleVisibility,
    trackOperation
  };

  return (
    <SyncContext.Provider value={value}>
      {children}
      <SyncIndicator />
    </SyncContext.Provider>
  );
};

const SyncIndicator = () => {
  const { operations } = useSyncStatus();
  const [showComplete, setShowComplete] = useState(false);
  const [wasSync, setWasSync] = useState(false);

  const pendingOps = operations.filter(op => op.status === 'pending');
  const hasPending = pendingOps.length > 0;

  // Handle completion state
  useEffect(() => {
    if (hasPending) {
      setWasSync(true);
      setShowComplete(false);
    } else if (wasSync && !hasPending) {
      // Just finished syncing
      setShowComplete(true);
      const timer = setTimeout(() => {
        setShowComplete(false);
        setWasSync(false);
      }, 2000); // Show checkmark for 2 seconds
      return () => clearTimeout(timer);
    }
  }, [hasPending, wasSync]);

  // Don't show anything if no sync is happening and not showing completion
  if (!hasPending && !showComplete) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2 flex items-center gap-2">
        {hasPending ? (
          <>
            {/* Rotating sync icon */}
            <div className="animate-spin">
              <Icon path={ICONS_MAP['sync']} className="w-5 h-5 text-blue-600" />
            </div>
            {/* Count */}
            <span className="text-sm font-semibold text-gray-700">{pendingOps.length}</span>
          </>
        ) : showComplete ? (
          <>
            {/* Checkmark icon */}
            <Icon path={ICONS_MAP['check']} className="w-5 h-5 text-green-600" />
            <span className="text-sm font-semibold text-green-600">Synced</span>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default SyncStatusProvider;

